import { Notifications } from "@result/rui/components";
import { useCallback, useMemo, useState } from "react";

import Authenticated from "#/components/Authenticated";
import Authorization from "#/components/Authorization";
import Preloader from "#/components/Preloader";
import { ScrollReset } from "#/components/ScrollReset";
import { GlobalContext } from "#/context";
import { useDrawerSwipe } from "#/hooks/useDrawerSwipe";
import { useWindowEvent } from "#/hooks/useWindowEvents";
import Header from "#/layout/Header";
import LeftSidebar, {
  MENU_STATE_COMPACT,
  MENU_STATE_EXPANDED,
  hasSidebarRail,
  initialMenuState,
} from "#/layout/LeftSidebar";
import DocumentGeneration from "#/pages/templates/DocumentGeneration";
import apiInstance from "#/redux/api/apiInstance";

const MainLayout = ({ children }) => {
  // @todo this could be moved to ApplicationSlice and useSelector could be used to
  // get the state instead of passing it down via context.
  const [globalState, setGlobalState] = useState({
    routes: [],
    breadcrumbs: [],
    notificationClosed: true,
    supportsOfflineMode: false,
  });

  const state = useMemo(
    () => [globalState, setGlobalState],
    [globalState, setGlobalState]
  );

  // Owned here because the narrow-layout toggle sits in the Header while the
  // menu it drives is the LeftSidebar.
  const [menuState, setMenuState] = useState(initialMenuState);

  // Edge swipe drives the same drawer as the ribbon's burger, so it only
  // applies where there is no rail and the sidebar is an overlay.
  useDrawerSwipe({
    isOpen: menuState === MENU_STATE_EXPANDED,
    onOpen: useCallback(() => setMenuState(MENU_STATE_EXPANDED), []),
    onClose: useCallback(() => setMenuState(MENU_STATE_COMPACT), []),
    isEnabled: useCallback(() => !hasSidebarRail(), []),
  });

  useWindowEvent();

  return (
    <GlobalContext.Provider value={state}>
      <ScrollReset>
        <Notifications>
          <DocumentGeneration>
            <Authenticated
              onToken={(token) => apiInstance.setAuthToken(token)}
              supportOfflineMode={state.supportsOfflineMode}>
              <Preloader>
                <Authorization>
                  <Header menuState={menuState} setMenuState={setMenuState} />
                  <LeftSidebar
                    menuState={menuState}
                    setMenuState={setMenuState}
                  />
                  {children}
                </Authorization>
              </Preloader>
            </Authenticated>
          </DocumentGeneration>
        </Notifications>
      </ScrollReset>
    </GlobalContext.Provider>
  );
};

export default MainLayout;
